import * as React from 'react';
import { connect } from 'react-redux';

import { Link } from 'libs/router';

import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import NotificationBadge from 'components/notification-badge/NotificationBadge';
import MoreButton from 'components/buttons/more-button/MoreButton';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import DuplicateCampaignModal from 'modules/campaigns/components/duplicate-campaign-modal/DuplicateCampaignModal';
import CampaignEditModal from 'modules/campaigns/components/CampaignEditModal';

import { __t, applyTranslations } from 'utils/language';
import { isAccepted } from 'utils/subcampaign';
import { getCampaignOfferUrl } from 'utils/campaign';
import { ORGANIZATION_PROFILE_TYPE_BRAND } from 'utils/organization';
import { insertIf } from 'utils/list';

import type {
  BrandProfile,
  CampaignOverview,
  CampaignWithSubcampaignMeta,
  ContextMenuItem,
  OrganizationProfileType
} from 'types';

applyTranslations(require('./i18n/campaigns-table-actions-cell.i18n.json'));

type StateProps = {
  unreadMessagesCount: number
};

type OwnProps = {
  campaign: CampaignWithSubcampaignMeta,
  overview: CampaignOverview | undefined | null,
  profileType: OrganizationProfileType,
  children?: React.ReactNode | null,
  hasCampaignLinks: boolean,
  hideMessengerLink?: boolean
};

type Props = StateProps & OwnProps;

type Action = {
  iconName: string,
  url: string,
  tooltip: string
};

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: false
});

const MESSAGES_ICON_NAME = 'speech-bubble-custom-bg';
const PAYMENTS_ICON_NAME = 'euro-inside-circle-custom-bg';

const msgKey = 'campaigns_table_actions_cell';

const getBrandActions = (campaign: CampaignWithSubcampaignMeta, overview?: CampaignOverview | null): Array<Action> => [
  {
    iconName: 'offers-custom-bg',
    url: getCampaignOfferUrl(String(campaign.id), overview),
    tooltip: __t(`${msgKey}.tooltips.offers`)
  },
  {
    iconName: MESSAGES_ICON_NAME,
    url: `/campaigns/${String(campaign.id)}/messages`,
    tooltip: __t(`${msgKey}.tooltips.messages`)
  },
  {
    iconName: 'images-custom-bg',
    url: `/campaigns/${String(campaign.id)}/deliverables`,
    tooltip: __t(`${msgKey}.tooltips.content`)
  },
  {
    iconName: 'bar-chart-custom-bg',
    url: `/campaigns/${String(campaign.id)}/report/v2`,
    tooltip: __t(`${msgKey}.tooltips.report`)
  },
  {
    iconName: PAYMENTS_ICON_NAME,
    url: `/campaigns/${String(campaign.id)}/payments`,
    tooltip: __t(`${msgKey}.tooltips.payments`)
  }
];

const getInfluencerActions = (campaign: CampaignWithSubcampaignMeta, hideMessengerLink?: boolean): Array<Action> => {
  const actions = [
    {
      iconName: 'brief-custom-bg',
      url: `/campaigns/${String(campaign.id)}/details/${String(campaign.subcampaignMeta && campaign.subcampaignMeta.id)}`,
      tooltip: __t(`${msgKey}.tooltips.brief`)
    }
  ];

  if (!hideMessengerLink) {
    actions.push({
      iconName: MESSAGES_ICON_NAME,
      url: `/campaigns/${String(campaign.id)}/details/${String(campaign.subcampaignMeta && campaign.subcampaignMeta.id)}/messages`,
      tooltip: __t(`${msgKey}.tooltips.messages`)
    });
  }

  if (campaign.subcampaignMeta && isAccepted(campaign.subcampaignMeta)) {
    actions.push({
      iconName: 'euro-inside-circle-custom-bg',
      url: `/campaigns/${String(campaign.id)}/details/${String(campaign.subcampaignMeta && campaign.subcampaignMeta.id)}/payments`,
      tooltip: __t(`${msgKey}.tooltips.payments`)
    });
  }

  return actions;
};

class CampaignsTableActionsCell extends React.PureComponent<Props> {
  getContextMenuItems(): Array<ContextMenuItem> {
    const { campaign, overview } = this.props;
    const brandProfile: BrandProfile | {} = campaign ? campaign.brandProfile : {};

    return [
      {
        type: 'modal',
        name: 'edit-campaign',
        text: __t(`${msgKey}.context_menu.edit_campaign`),
        meta: {
          campaignId: campaign.id,
          campaignOverview: overview,
          brandProfileId: ('id' in brandProfile) ? brandProfile.id : undefined,
          triggerType: 'context-menu'
        },
        extra: {
          component: CampaignEditModal
        }
      },
      ...insertIf(overview?.contentApprovalV2, {
        name: 'duplicate-campaign',
        type: 'modal',
        text: __t(`${msgKey}.context_menu.duplicate_campaign`),
        meta: {
          campaign
        },
        extra: {
          component: DuplicateCampaignModal
        }
      })
    ];
  }

  renderContextMenu(): React.ReactNode | undefined | null {
    return (
      <div
        className="campaigns-table-actions-cell__context-menu"
      >
        <MoreButtonWithMenu
          width={40}
          contextMenuProps={{ items: this.getContextMenuItems() }}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const {
      children,
      campaign,
      profileType,
      overview,
      unreadMessagesCount,
      hasCampaignLinks,
      hideMessengerLink
    } = this.props;

    let actions: Array<Action> = profileType === ORGANIZATION_PROFILE_TYPE_BRAND ?
      getBrandActions(campaign, overview) :
      getInfluencerActions(campaign, hideMessengerLink);

    if (!hasCampaignLinks) {
      // drop all links and reset tooltips if no actions are available
      actions = actions.map(action => ({
        ...action,
        url: '',
        tooltip: __t('campaigns_table_actions_cell.tooltips.not_available')
      }));
    }

    return (
      <div className="campaigns-table-actions-cell">
        <div className="campaigns-table-actions-cell__items">
          {
            actions.map(action => (
              <div
                className="campaigns-table-actions-cell__item"
                key={action.iconName}
              >
                <Tooltip
                  message={action.tooltip}
                  width="auto-compact"
                >
                  <Link to={action.url}>
                    <SVGDynamicIcon
                      name={action.iconName}
                      customColor="grey"
                      clickable
                      width={22}
                      height={22}
                    />
                  </Link>
                </Tooltip>
                {
                  action.iconName === MESSAGES_ICON_NAME && unreadMessagesCount > 0 && (
                    <div className="campaigns-table-actions-cell__badge">
                      <NotificationBadge
                        value={unreadMessagesCount}
                        size="small"
                      />
                    </div>
                  )
                }
                {
                  action.iconName === PAYMENTS_ICON_NAME && Boolean(campaign.budgetNote) && (
                    <div className="campaigns-table-actions-cell__notification" />
                  )
                }
              </div>
            ))
          }
        </div>
        {profileType === ORGANIZATION_PROFILE_TYPE_BRAND && this.renderContextMenu()}
        {children}
      </div>
    );
  }
}

const mapStateToProps = ({ unreadMessages }, { campaign }): StateProps => {
  const campaignId = campaign.id;

  const unreadMessagesCount = (unreadMessages.conversations || [])
    .find((entry) => entry.campaignId === campaignId)
    ?.unreadConversationsCount ?? 0;

  return {
    unreadMessagesCount
  };
};

export default connect(mapStateToProps)(CampaignsTableActionsCell);
